<script setup lang="ts">
import { computed } from 'vue'
import { Clipboard } from 'lucide-vue-next'

import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppTagStatus from '@/components/atoms/tag/AppTagStatus.vue'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    title: string
    content?: string
    status?: string
    statusColorMap?: Record<
      string,
      'secondary' | 'success' | 'info' | 'warn' | 'warning' | 'danger' | 'contrast'
    >
    statusLabelMap?: Record<string, string>
    loading?: boolean
    error?: string | null
  }>(),
  {
    content: '',
    status: '',
    statusColorMap: () => ({}),
    statusLabelMap: () => ({}),
    loading: false,
    error: null,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  copy: []
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const showProgress = computed(
  () => props.loading || (props.status && !['ready', 'failed', 'disabled'].includes(props.status)),
)

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const renderInline = (value: string) =>
  escapeHtml(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')

const renderedContent = computed(() => {
  const lines = (props.content || '').split(/\r?\n/)
  const html: string[] = []
  let listType: 'ul' | 'ol' | null = null

  const closeList = () => {
    if (!listType) return
    html.push(`</${listType}>`)
    listType = null
  }

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) {
      closeList()
      continue
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (heading) {
      closeList()
      const level = heading[1].length
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`)
      continue
    }

    const unordered = trimmed.match(/^[-*]\s+(.+)$/)
    if (unordered) {
      if (listType !== 'ul') {
        closeList()
        html.push('<ul>')
        listType = 'ul'
      }
      html.push(`<li>${renderInline(unordered[1])}</li>`)
      continue
    }

    const ordered = trimmed.match(/^\d+\.\s+(.+)$/)
    if (ordered) {
      if (listType !== 'ol') {
        closeList()
        html.push('<ol>')
        listType = 'ol'
      }
      html.push(`<li>${renderInline(ordered[1])}</li>`)
      continue
    }

    closeList()
    html.push(`<p>${renderInline(trimmed)}</p>`)
  }

  closeList()
  return html.join('')
})
</script>

<template>
  <AppDialog
    v-model="isOpen"
    :header="title"
    custom-class="backdrop-blur-xl!"
    width="72vw"
    :breakpoints="{ '1199px': '85vw', '575px': '94vw' }"
    :maximizable="true"
  >
    <template #header>
      <div class="flex min-w-0 items-center gap-3">
        <span class="truncate text-base font-semibold text-slate-800 dark:text-slate-100">
          {{ title }}
        </span>
        <AppTagStatus
          v-if="status"
          :status="status"
          :color-map="statusColorMap"
          :label-map="statusLabelMap"
        />
      </div>
    </template>

    <template #content>
      <section class="flex max-h-[72vh] flex-col gap-4">
        <div
          v-if="showProgress"
          class="rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40"
        >
          <div class="mb-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div class="h-full w-2/3 animate-pulse rounded-full bg-[var(--p-primary-color)]" />
          </div>
          <p class="text-sm text-slate-600 dark:text-slate-300">
            El documento se está procesando. El contenido aparecerá cuando esté listo.
          </p>
        </div>

        <p v-if="error" class="text-sm text-red-600 dark:text-red-400">
          {{ error }}
        </p>

        <div v-if="content && !loading" class="relative min-h-0">
          <button
            type="button"
            aria-label="Copiar"
            class="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md border border-slate-200 bg-white/95 text-slate-600 shadow-sm transition hover:border-[var(--p-primary-color)] hover:text-[var(--p-primary-color)] dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-200"
            v-tooltip.top="'Copiar'"
            @click="emit('copy')"
          >
            <Clipboard class="h-4 w-4" />
          </button>

          <article
            class="markdown-preview max-h-[64vh] overflow-auto rounded-md border border-slate-200 bg-white p-4 pr-12 text-[0.85rem] leading-relaxed text-slate-800 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            v-html="renderedContent"
          />
        </div>
      </section>
    </template>
  </AppDialog>
</template>

<style scoped>
.markdown-preview :deep(h1),
.markdown-preview :deep(h2),
.markdown-preview :deep(h3),
.markdown-preview :deep(h4) {
  margin: 1rem 0 0.5rem;
  color: var(--p-primary-color);
  font-weight: 700;
  line-height: 1.2;
}

.markdown-preview :deep(h1) {
  font-size: 1.2rem;
}

.markdown-preview :deep(h2) {
  font-size: 1.05rem;
}

.markdown-preview :deep(h3) {
  font-size: 0.98rem;
}

.markdown-preview :deep(p) {
  margin: 0.5rem 0;
}

.markdown-preview :deep(ul),
.markdown-preview :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.4rem;
}

.markdown-preview :deep(li) {
  margin: 0.25rem 0;
}

.markdown-preview :deep(blockquote) {
  margin: 0.9rem 0;
  border-left: 3px solid var(--p-primary-color);
  padding: 0.25rem 0 0.25rem 0.9rem;
  color: rgb(71 85 105);
}

.markdown-preview :deep(code) {
  border-radius: 0.25rem;
  background: rgb(241 245 249);
  padding: 0.1rem 0.25rem;
  font-size: 0.82em;
}

.markdown-preview :deep(pre) {
  overflow: auto;
  border-radius: 0.5rem;
  background: rgb(15 23 42);
  padding: 0.75rem;
  color: white;
}

.markdown-preview :deep(pre code) {
  background: transparent;
  padding: 0;
}

.markdown-preview :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.82rem;
}

.markdown-preview :deep(th),
.markdown-preview :deep(td) {
  border: 1px solid rgb(226 232 240);
  padding: 0.5rem;
  text-align: left;
  vertical-align: top;
}

.markdown-preview :deep(th) {
  background: rgb(248 250 252);
}
</style>
